import { forwardRef } from "react";
import { Loader2, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Canonical Button.
 *
 * Replaces the dozens of inline `inline-flex items-center gap-1.5 rounded-lg
 * bg-brand-grad …` button blocks scattered across pages. Variants encode the
 * action hierarchy described in docs/ui-conventions.md:
 *
 *   primary    — single per surface (Save, Run Fix, Approve); brand outline
 *   secondary  — outline (Cancel, secondary action)
 *   ghost      — text-only (Open, Inspect, links inside dense rows)
 *   danger     — destructive confirm (Delete, Discard)
 *
 * Sizes match the operator-density chrome — `sm` (h-7) for inline / row
 * actions, `md` (h-8) for toolbars, `lg` (h-9) for primary page CTAs.
 *
 * Always render a real <button>; never a styled <div> — keyboard, focus
 * ring, and form-submit semantics depend on it.
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANT_CLS: Record<ButtonVariant, string> = {
  // Brand-tinted OUTLINE, not a filled gradient. The sidebar's always-visible
  // "New issue" button has read this way for as long as it existed, while the
  // page CTA next to it shouted in `bg-brand-grad` + `shadow-glow` — two
  // different answers to "this is the primary action here" on one screen.
  // The quiet one wins: it is the same register as the rest of the operator
  // chrome (quiet surfaces, no nested fills), and it leaves the gradient to
  // the places that are genuinely singular — the marketing pages and the
  // tenant switcher.
  primary:
    "border border-brand-500/30 bg-brand-500/[0.08] text-brand-200 hover:bg-brand-500/[0.14] hover:border-brand-500/50 hover:text-brand-100",
  secondary:
    "border border-line-subtle text-fg-dim hover:text-fg hover:bg-bg-elev hover:border-line",
  ghost:
    "text-fg-dim hover:text-fg hover:bg-bg-hover",
  danger:
    "border border-status-critical/40 text-status-critical hover:bg-status-critical/10",
};

const SIZE_CLS: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-[12.5px] gap-1.5 rounded-md",
  md: "h-8 px-3 text-[13px] gap-1.5 rounded-md",
  lg: "h-9 px-3.5 text-[13.5px] gap-2 rounded-lg",
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 12, md: 13, lg: 14 };

export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "prefix"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Lucide icon rendered before the label. Sized to match the button. */
  icon?: LucideIcon;
  /** Lucide icon rendered after the label. */
  trailingIcon?: LucideIcon;
  /** When true, swaps the leading icon for a spinner and disables the button. */
  loading?: boolean;
  /** When true, removes width constraints so the button stretches to its container. */
  block?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    icon: Icon,
    trailingIcon: TrailingIcon,
    loading = false,
    block = false,
    disabled,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  const iconSize = ICON_SIZE[size];
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center font-medium whitespace-nowrap transition focus-ring",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        SIZE_CLS[size],
        VARIANT_CLS[variant],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 size={iconSize} className="animate-spin" />
      ) : Icon ? (
        <Icon size={iconSize} />
      ) : null}
      {children}
      {TrailingIcon && <TrailingIcon size={iconSize} />}
    </button>
  );
});
